<template>
  <div class="order-container">
    <!-- 筛选条件 -->
    <el-card class="filter-card" shadow="never">
      <el-form :inline="true" :model="filters" class="filter-form">
        <!-- 订单号 -->
        <el-form-item label="订单号">
          <el-input
            v-model="filters.order_no"
            placeholder="请输入订单号"
            clearable
            style="width: 200px"
            @keyup.enter.native="handleFilter"
          />
        </el-form-item>

        <!-- ICCID -->
        <el-form-item label="ICCID">
          <el-input
            v-model="filters.iccid"
            placeholder="请输入ICCID"
            clearable
            style="width: 200px"
            @keyup.enter.native="handleFilter"
          />
        </el-form-item>

        <!-- 支付状态 -->
        <el-form-item label="支付状态">
          <el-select
            v-model="filters.payment_status"
            placeholder="请选择"
            clearable
            style="width: 150px"
          >
            <el-option label="全部" :value="-1" />
            <el-option label="待支付" :value="0" />
            <el-option label="已支付" :value="1" />
            <el-option label="支付失败" :value="2" />
          </el-select>
        </el-form-item>

        <!-- 充值状态 -->
        <el-form-item label="充值状态">
          <el-select
            v-model="filters.recharge_status"
            placeholder="请选择"
            clearable
            style="width: 150px"
          >
            <el-option label="全部" :value="-1" />
            <el-option label="待充值" :value="0" />
            <el-option label="充值成功" :value="1" />
            <el-option label="充值失败" :value="2" />
          </el-select>
        </el-form-item>

        <!-- 退款状态 -->
        <el-form-item label="退款状态">
          <el-select
            v-model="filters.refund_status"
            placeholder="请选择"
            clearable
            style="width: 150px"
          >
            <el-option label="全部" :value="-1" />
            <el-option label="无退款" :value="0" />
            <el-option label="退款中" :value="1" />
            <el-option label="已退款" :value="2" />
            <el-option label="退款失败" :value="3" />
          </el-select>
        </el-form-item>

        <!-- 操作按钮 -->
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleFilter">
            查询
          </el-button>
          <el-button icon="el-icon-refresh-left" @click="handleReset">
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 订单列表 -->
    <div v-loading="listLoading" class="order-list">
      <el-card
        v-for="(order, index) in orderList"
        :key="order.order_no"
        class="order-card"
        :class="getOrderCardClass(order, index)"
        shadow="hover"
      >
        <!-- 第一行：订单号、ICCID、套餐名称、退款状态 -->
        <div class="order-row row-main">
          <div class="order-item">
            <span class="label">订单号:</span>
            <el-link type="primary">{{ order.order_no }}</el-link>
          </div>
          <div class="order-item">
            <span class="label">ICCID:</span>
            <span class="value" :title="order.iccid">{{ order.iccid }}</span>
          </div>
          <div class="order-item">
            <span class="label">套餐名称:</span>
            <span class="value" :title="order.package_name">{{ order.package_name }}</span>
          </div>
          <div class="order-item">
            <span class="label">退款状态:</span>
            <el-tag :type="getOrderRefundStatusTagType(order.refund_status)" size="small">
              {{ getOrderRefundStatusText(order.refund_status) }}
            </el-tag>
          </div>
        </div>

        <!-- 第二行：订单金额、商品、收款账户、店铺名称 -->
        <div class="order-row">
          <div class="order-item">
            <span class="label">订单金额:</span>
            <span class="value price">¥{{ formatMoney(order.price) }}</span>
          </div>
          <div class="order-item">
            <span class="label">商品:</span>
            <span class="value" :title="order.product_name">{{ order.product_name || '-' }}</span>
          </div>
          <div class="order-item">
            <span class="label">收款账户:</span>
            <span class="value">{{ order.merchant_name || '-' }}</span>
          </div>
          <div class="order-item">
            <span class="label">店铺名称:</span>
            <span class="value">{{ order.shop_name || '-' }}</span>
          </div>
        </div>

        <!-- 第三行：支付状态、充值状态、创建时间、操作按钮 -->
        <div class="order-row">
          <div class="order-item">
            <span class="label">支付状态:</span>
            <el-tag :type="getPaymentStatusTagType(order.payment_status)" size="small">
              {{ getPaymentStatusText(order.payment_status) }}
            </el-tag>
          </div>
          <div class="order-item">
            <span class="label">充值状态:</span>
            <el-tag :type="getRechargeStatusTagType(order.recharge_status)" size="small">
              {{ getRechargeStatusText(order.recharge_status) }}
            </el-tag>
          </div>
          <div class="order-item">
            <span class="label">创建时间:</span>
            <span class="value">{{ formatDate(order.created_at) }}</span>
          </div>
          <div class="order-item actions">
            <el-dropdown @command="handleCommand($event, order)" trigger="click">
              <el-button type="primary" size="small">
                操作 <i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <el-dropdown-item
                  v-if="order.payment_status === 1 && order.recharge_status === 0"
                  command="updateRecharge"
                  icon="el-icon-edit"
                >
                  更新充值状态
                </el-dropdown-item>
                <el-dropdown-item
                  v-if="order.payment_status === 1 && order.refund_status === 0"
                  command="applyRefund"
                  icon="el-icon-money"
                >
                  申请退款
                </el-dropdown-item>
                <el-dropdown-item
                  v-if="order.refund_status > 0"
                  command="viewRefund"
                  icon="el-icon-view"
                >
                  查看退款
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </el-card>

      <!-- 空状态 -->
      <div v-if="!listLoading && orderList.length === 0" class="empty-state">
        <el-empty description="暂无订单数据"></el-empty>
      </div>
    </div>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        :current-page="listQuery.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="listQuery.page_size"
        :total="total"
        background
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 更新充值状态弹窗 -->
    <recharge-status-dialog
      ref="rechargeDialog"
      @success="fetchOrderList"
    />

    <!-- 申请退款弹窗 -->
    <refund-apply-dialog
      ref="refundDialog"
      @success="fetchOrderList"
    />
  </div>
</template>

<script>
import { getOrderList } from '@/api/order'
import RechargeStatusDialog from './components/RechargeStatusDialog'
import RefundApplyDialog from './components/RefundApplyDialog'
import {
  getPaymentStatusText,
  getPaymentStatusTagType,
  getRechargeStatusText,
  getRechargeStatusTagType,
  getOrderRefundStatusText,
  getOrderRefundStatusTagType,
  formatMoney,
  formatDate
} from '@/utils/iccid-formatter'

export default {
  name: 'IccidOrderList',
  components: {
    RechargeStatusDialog,
    RefundApplyDialog
  },
  data() {
    return {
      orderList: [],
      total: 0,
      listLoading: false,
      listQuery: {
        page: 1,
        page_size: 20
      },
      filters: {
        order_no: '',
        iccid: '',
        payment_status: -1,
        recharge_status: -1,
        refund_status: -1
      }
    }
  },
  created() {
    this.fetchOrderList()
  },
  methods: {
    // 格式化函数
    formatMoney,
    formatDate,
    getPaymentStatusText,
    getPaymentStatusTagType,
    getRechargeStatusText,
    getRechargeStatusTagType,
    getOrderRefundStatusText,
    getOrderRefundStatusTagType,

    // 获取订单列表
    async fetchOrderList() {
      this.listLoading = true
      try {
        const params = {
          ...this.listQuery,
          ...this.filters
        }
        const { data } = await getOrderList(params)
        this.orderList = data.list || []
        this.total = data.total || 0
      } catch (error) {
        this.$message.error('获取订单列表失败')
      } finally {
        this.listLoading = false
      }
    },

    // 查询
    handleFilter() {
      this.listQuery.page = 1
      this.fetchOrderList()
    },

    // 重置
    handleReset() {
      this.filters = {
        order_no: '',
        iccid: '',
        payment_status: -1,
        recharge_status: -1,
        refund_status: -1
      }
      this.listQuery.page = 1
      this.fetchOrderList()
    },

    // 分页
    handleSizeChange(val) {
      this.listQuery.page_size = val
      this.fetchOrderList()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.fetchOrderList()
    },

    // 处理下拉菜单命令
    handleCommand(command, row) {
      switch (command) {
        case 'updateRecharge':
          this.handleUpdateRecharge(row)
          break
        case 'applyRefund':
          this.handleRefund(row)
          break
        case 'viewRefund':
          this.handleRefundDetail(row)
          break
      }
    },

    // 更新充值状态
    handleUpdateRecharge(row) {
      this.$refs.rechargeDialog.open(row)
    },

    // 申请退款
    handleRefund(row) {
      this.$refs.refundDialog.open(row)
    },

    // 查看退款详情
    handleRefundDetail(row) {
      this.$router.push({
        name: 'IccidRefundList',
        query: { order_no: row.order_no }
      })
    },

    // 获取订单卡片样式类
    getOrderCardClass(order, index) {
      const classes = []

      if (order.refund_status > 0) {
        classes.push('card-refund')
      } else if (order.payment_status === 1 && order.recharge_status === 1) {
        classes.push('card-success')
      } else if (order.payment_status === 1 && order.recharge_status === 0) {
        classes.push('card-pending')
      } else if (order.payment_status === 0) {
        classes.push('card-unpaid')
      } else if (order.payment_status === 2 || order.recharge_status === 2) {
        classes.push('card-failed')
      }

      return classes.join(' ')
    }
  }
}
</script>

<style lang="scss" scoped>
.order-container {
  padding: 20px;
}

.filter-card {
  margin-bottom: 20px;
}

.filter-form {
  margin-bottom: -18px;
}

/* 订单列表 */
.order-list {
  min-height: 400px;
}

.order-card {
  margin-bottom: 16px;
  transition: all 0.3s;
  border-left: 4px solid transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:last-child {
    margin-bottom: 0;
  }

  /* 状态颜色 */
  &.card-success {
    border-left-color: #67C23A;
    background: linear-gradient(to right, rgba(103, 194, 58, 0.02), transparent);
  }

  &.card-pending {
    border-left-color: #E6A23C;
    background: linear-gradient(to right, rgba(230, 162, 60, 0.02), transparent);
  }

  &.card-unpaid {
    border-left-color: #909399;
    background: linear-gradient(to right, rgba(144, 147, 153, 0.02), transparent);
  }

  &.card-refund {
    border-left-color: #9C27B0;
    background: linear-gradient(to right, rgba(156, 39, 176, 0.02), transparent);
  }

  &.card-failed {
    border-left-color: #F56C6C;
    background: linear-gradient(to right, rgba(245, 108, 108, 0.02), transparent);
  }
}

/* 订单行 */
.order-row {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &.row-main {
    padding-top: 0;

    .value {
      font-weight: 500;
    }
  }
}

.order-item {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 0 12px;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }

  &.actions {
    flex: 0 0 auto;
    justify-content: flex-end;
  }

  .label {
    color: #909399;
    font-size: 13px;
    margin-right: 8px;
    white-space: nowrap;
  }

  .value {
    color: #303133;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.price {
      color: #F56C6C;
      font-weight: 600;
      font-size: 14px;
    }

    &.commission {
      color: #67C23A;
      font-weight: 600;
      font-size: 14px;
    }
  }

  ::v-deep .el-link {
    font-size: 13px;
    font-weight: 500;
  }

  ::v-deep .el-tag {
    margin-left: 0;
  }
}

/* 空状态 */
.empty-state {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
}

/* 分页 */
.pagination-container {
  margin-top: 20px;
  text-align: right;
}
</style>
